/**
 * רשימת לקוחות — TASKS 5.4. עימוד שרת (25 לדף), סינון וחיפוש, יצירה ישירה.
 *
 * ⭐ החיפוש ("q") מתנהג בדיוק כמו בשרת: שם/חברה/דוא"ל ב-LIKE, וטלפון ⛔ רק
 *    אחרי נרמול מלא (`GET /api/admin/customers?q=`) — ⛔ לא לפי `phone_raw`.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { AdminNav } from './AdminNav.js';
import { AdminPageHeader } from './AdminPageHeader.js';
import { isAdminApiError } from './adminApi.js';
import { createCustomer, listCustomers, type CustomerListItem } from './customersApi.js';

export function CustomersPage() {
  const [customers, setCustomers] = useState<CustomerListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [isRegularFilter, setIsRegularFilter] = useState<'all' | 'regular' | 'other'>('all');
  const [q, setQ] = useState('');
  const [listError, setListError] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function refetch() {
    setListError(null);
    try {
      const result = await listCustomers({
        page,
        isRegular: isRegularFilter === 'all' ? undefined : isRegularFilter === 'regular',
        q: q.trim() || undefined,
      });
      setCustomers(result.customers);
      setTotal(result.total);
      setPageSize(result.pageSize);
    } catch (err) {
      setListError(isAdminApiError(err) ? err.message : 'שגיאה בטעינת הלקוחות.');
    }
  }

  useEffect(() => {
    void refetch();
  }, [page, isRegularFilter]);

  function handleSearchSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(1);
    void refetch();
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await createCustomer({
        fullName: fullName.trim(),
        phone: phone.trim(),
        company: company.trim() || undefined,
      });
      setFullName('');
      setPhone('');
      setCompany('');
      setPage(1);
      await refetch();
    } catch (err) {
      setFormError(isAdminApiError(err) ? err.message : 'שגיאה ביצירת הלקוח.');
    } finally {
      setSubmitting(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <AdminNav />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <AdminPageHeader title="לקוחות" description="חיפוש וניהול לקוחות, כולל היסטוריית הצעות." />

        <section
          className="border-line bg-surface mt-6 rounded-lg border p-6"
          aria-labelledby="new-cust-h"
        >
          <h2 id="new-cust-h" className="text-lg font-semibold">
            לקוח חדש
          </h2>

          <form
            className="mt-4 grid gap-4 sm:grid-cols-[2fr_1fr_1fr_auto]"
            onSubmit={(e) => void handleCreate(e)}
          >
            <div>
              <label htmlFor="cust-name" className="block text-sm font-semibold">
                שם מלא
              </label>
              <input
                id="cust-name"
                required
                aria-required="true"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <div>
              <label htmlFor="cust-phone" className="block text-sm font-semibold">
                טלפון
              </label>
              <input
                id="cust-phone"
                required
                aria-required="true"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="ltr-inline mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <div>
              <label htmlFor="cust-company" className="block text-sm font-semibold">
                חברה (אופציונלי)
              </label>
              <input
                id="cust-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <div className="flex items-end">
              <button type="submit" className="btn btn-cta" disabled={submitting}>
                {submitting ? 'מוסיף…' : 'הוסף'}
              </button>
            </div>
          </form>

          {formError && (
            <p role="alert" className="text-danger mt-3 text-sm">
              {formError}
            </p>
          )}
        </section>

        <form
          className="mt-6 grid gap-4 sm:grid-cols-[2fr_1fr_auto]"
          onSubmit={handleSearchSubmit}
          role="search"
        >
          <div>
            <label htmlFor="cust-q" className="block text-sm font-semibold">
              חיפוש (שם, חברה, דוא&quot;ל או טלפון)
            </label>
            <div className="relative mt-1">
              <MagnifyingGlass
                size={16}
                weight="regular"
                aria-hidden="true"
                className="text-muted pointer-events-none absolute start-3 top-1/2 -translate-y-1/2"
              />
              <input
                id="cust-q"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-md border border-line py-2 pe-3 ps-9"
              />
            </div>
          </div>

          <div>
            <label htmlFor="cust-regular" className="block text-sm font-semibold">
              סוג לקוח
            </label>
            <select
              id="cust-regular"
              value={isRegularFilter}
              onChange={(e) => {
                setPage(1);
                setIsRegularFilter(e.target.value as 'all' | 'regular' | 'other');
              }}
              className="mt-1 w-full rounded-md border border-line px-3 py-2"
            >
              <option value="all">הכול</option>
              <option value="regular">לקוחות קבועים</option>
              <option value="other">שאינם קבועים</option>
            </select>
          </div>

          <div className="flex items-end">
            <button type="submit" className="btn btn-secondary">
              חיפוש
            </button>
          </div>
        </form>

        {listError && (
          <p role="alert" className="text-danger mt-4 text-sm">
            {listError}
          </p>
        )}

        {customers === null && !listError && <p className="text-muted mt-6">טוען…</p>}
        {customers && customers.length === 0 && <p className="text-muted mt-6">לא נמצאו לקוחות.</p>}

        {customers && customers.length > 0 && (
          <>
            <table className="admin-table mt-6 w-full border-collapse text-sm">
              <caption className="sr-only">
                רשימת לקוחות, עמוד {page} מתוך {totalPages}
              </caption>
              <thead>
                <tr className="border-line border-b text-start">
                  <th scope="col" className="p-2 text-start">
                    שם
                  </th>
                  <th scope="col" className="p-2 text-start">
                    טלפון
                  </th>
                  <th scope="col" className="p-2 text-start">
                    חברה
                  </th>
                  <th scope="col" className="p-2 text-start">
                    סוג
                  </th>
                  <th scope="col" className="p-2 text-start">
                    הצעות
                  </th>
                  <th scope="col" className="p-2 text-start">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id} className="border-line border-b">
                    <td className="p-2">{customer.full_name}</td>
                    <td className="p-2">
                      <span className="ltr-inline">{customer.phone_e164}</span>
                    </td>
                    <td className="p-2">{customer.company ?? '-'}</td>
                    <td className="p-2">{customer.is_regular ? 'קבוע' : 'רגיל'}</td>
                    <td className="tabular p-2">{customer.quote_count}</td>
                    <td className="p-2">
                      <Link
                        to={`/admin/customers/${customer.id}`}
                        className="rounded border border-line px-2 py-1"
                      >
                        כרטיס לקוח
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <nav aria-label="עימוד רשימת לקוחות" className="mt-4 flex items-center gap-3">
              <button
                type="button"
                className="rounded border border-line px-3 py-1 disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                הקודם
              </button>
              <span className="text-sm">
                עמוד {page} מתוך {totalPages} ({total} לקוחות)
              </span>
              <button
                type="button"
                className="rounded border border-line px-3 py-1 disabled:opacity-40"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                הבא
              </button>
            </nav>
          </>
        )}
      </main>
    </>
  );
}
